import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import { UntypedFormGroup, NgForm, ValidationErrors, FormControl, Validators } from '@angular/forms';
import { ApBaseComponent } from '../ap-base-component/index';

@Component({
  selector: 'ap-file-upload',
  templateUrl: './ap-file-upload.component.html',
})
export class ApFileUploadComponent extends ApBaseComponent implements OnInit, OnChanges, Validators {
  @Output() filesChanged = new EventEmitter<File[]>();
  @Output() errorOutput = new EventEmitter<string>();

  // Regex of Extensions allowed
  @Input() baseId: string;
  @Input() allowedExtensions?: string;
  @Input() fileSizeLimit?: number | null;
  @Input() form?: NgForm;
  @Input() formGroup?: UntypedFormGroup;
  @Input() controlName: string;
  @Input() required?: boolean;
  @Input() fileUploadControlDisplayName: string;
  @Input() maxFiles?: number | null;
  @Input() disabled?: boolean;
  @Input() disableChips = false;
  @Input() formGroupSubmitted?: boolean;
  @Input() showRequiredOnly = false;

  @ViewChild('uploadInput', { static: true }) uploadInput: ElementRef;

  Id: string;
  chips: File[] = [];

  fileUploadControlFormControl: FormControl<File[]>;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setId();
    this.initializeFormControl();
  }

  inputUpload() {
    if (this.disabled) { return; }
    this.uploadInput.nativeElement.click();
  }

  removeFile(file: File) {
    this.chips.splice(this.chips.indexOf(file), 1);
    this.updateFormControl();
    this.filesChanged.emit(this.chips);
  }

  private getAllowedExtensionsRegex() {
    if (!this.allowedExtensions) {
      return undefined;
    }

    const regexString = this.allowedExtensions.split(',').map(
      (value) => value.trim().toLowerCase().replace('.', '\.')
    ).join('|');
    return regexString;
  }

  selectFiles(fileUploadEvent: any) {
    const fileList: FileList = fileUploadEvent.target.files;
    const allowedExtensionsRegex = this.getAllowedExtensionsRegex();

    /* eslint-disable @typescript-eslint/prefer-for-of */
    for (let index = 0; index < fileList.length; index++) {
      if (allowedExtensionsRegex) {
        let extension = '.' + fileList[index].name.split('.').pop();

        if (extension) {
          extension = extension.toLowerCase();
        }

        const matches = extension.match(allowedExtensionsRegex);
        if (!matches || matches.length < 1) {
          this.errorOutput.emit('File(s) cannot be upload due to file type restriction');
          continue;
        }
      }

      if (this.fileSizeLimit && fileList[index].size > (1000 * this.fileSizeLimit)) {
        this.errorOutput.emit('File size is over the limit of ' + this.fileSizeLimit + ' kb');
        continue;
      }

      if (this.maxFiles && this.chips.length >= this.maxFiles) {
        if (this.maxFiles === 1) {
          this.chips.splice(0, this.chips.length);
        } else {
          this.errorOutput.emit('Maximum number of files uploaded reached (' + this.maxFiles + ')');
          continue;
        }
      }

      this.chips.push(fileList[index]);
      this.errorOutput.emit();
    }

    this.updateFormControl();
    this.filesChanged.emit(this.chips);
  }

  initializeFormControl() {
    const formGroup = this.form?.form ?? this.formGroup;
    if (!formGroup) { return; }

    const controlName = this.controlName ? this.controlName : this.Id;
    if (formGroup.contains(controlName)) {
      this.fileUploadControlFormControl = formGroup.get(controlName) as FormControl<File[]>;
      this.fileUploadControlFormControl.addValidators([this.validate]);
    }
    else {
      this.fileUploadControlFormControl = new FormControl<File[]>(this.chips ?? [], this.validate) as FormControl<File[]>;
      formGroup.addControl(controlName, this.fileUploadControlFormControl);
    }
    this.updateFormControl();
  }

  private updateFormControl() {
    if (!this.fileUploadControlFormControl) { return; }
    if (this.fileUploadControlFormControl.value !== this.chips) {
      this.fileUploadControlFormControl.setValue(this.chips);
    }
    this.fileUploadControlFormControl.updateValueAndValidity();
  }

  validate = (): ValidationErrors | null => {
    const valid = this.getIsValid();
    return valid ? null : {
      required: true,
    };
  };

  private getIsValid(): boolean {
    const isValid = !this.required || !!(this.chips && this.chips.length);
    return isValid;
  }

  isRequired(): boolean {
    return !this.required ? false : this.required;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.baseId && changes.baseId.currentValue) {
      this.baseId = changes.baseId.currentValue;
      this.setId();
    }

    if (changes.required && changes.required.currentValue !== undefined) {
      this.required = changes.required.currentValue;
      this.updateFormControl();
    }

    if (changes.disabled) {
      this.updateIsDisabled();
    }
  }

  private updateIsDisabled(): void {
    if (this.fileUploadControlFormControl && this.disabled !== this.fileUploadControlFormControl.disabled) {
      if (this.fileUploadControlFormControl.enabled) {
        this.fileUploadControlFormControl.disable();
      }
      else {
        this.fileUploadControlFormControl.enable();
      }
    }
  }

  private setId() {
    this.Id = `${this.baseId}-fileupload`;
  }
}
